import { Utils } from "@tldraw/core";
import Vec from "@tldraw/vec";
import type { ShapeStyles } from "pages/flyer/builder/drawer/types";
import getStroke, { getStrokePoints } from "perfect-freehand";
import { getShapeStyle } from "../shared";
import { getOffsetPolygon } from "../shared/PolygonUtils";

export function getHexagonPoints(size: number[], offset = 0, rotation = 0) {
  const [w, h] = size;
  const points = [
    [w / 2, 0],
    [w, h / 4],
    [w, (3 * h) / 4],
    [w / 2, h],
    [0, (3 * h) / 4],
    [0, h / 4],
  ];
  let adjustedPoints = points;
  if (offset) adjustedPoints = getOffsetPolygon(points, offset);
  if (rotation) adjustedPoints = adjustedPoints.map((pt) => Vec.rotWith(pt, [w / 2, h / 2], rotation));

  return adjustedPoints;
}

export function getHexagonCentroid(size: number[]) {
  const points = getHexagonPoints(size);
  const centroid = points.reduce((acc, pt) => [acc[0] + pt[0] / points.length, acc[1] + pt[1] / points.length], [0, 0]);
  return centroid;
}

function getHexagonDrawPoints(id: string, size: number[], strokeWidth: number) {
  const points = getHexagonPoints(size);
  const getRandom = Utils.rng(id);
  const offsets = Array.from(Array(6)).map(() => {
    return [getRandom() * strokeWidth * 0.75, getRandom() * strokeWidth * 0.75];
  });
  const corners = points.map((pt, i) => Vec.add(pt, offsets[i]));
  const rm = Math.round(Math.abs(getRandom() * 2 * 6));
  const lines = Utils.rotateArray(
    corners.map((pt, i) => Vec.pointsBetween(pt, corners[(i + 1) % corners.length], 32)),
    rm
  );
  const drawPoints = [...lines.flat(), ...lines[0]];
  return {
    points: drawPoints,
  };
}

function getDrawStrokeInfo(id: string, size: number[], style: ShapeStyles) {
  const { strokeWidth } = getShapeStyle(style);
  const { points } = getHexagonDrawPoints(id, size, strokeWidth!);
  const options = {
    size: strokeWidth,
    thinning: 0.65,
    streamline: 0.3,
    smoothing: 1,
    simulatePressure: false,
    last: true,
  };
  return { points, options };
}

export function getHexagonPath(id: string, size: number[], style: ShapeStyles) {
  const { points, options } = getDrawStrokeInfo(id, size, style);
  const stroke = getStroke(points, options);
  return Utils.getSvgPathFromStroke(stroke);
}

export function getHexagonIndicatorPathTDSnapshot(id: string, size: number[], style: ShapeStyles) {
  const { points, options } = getDrawStrokeInfo(id, size, style);
  const strokePoints = getStrokePoints(points, options);
  return Utils.getSvgPathFromStroke(
    strokePoints.map((pt) => pt.point.slice(0, 2)),
    false
  );
}
